import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, LayoutGrid, Layers, HardDrive } from "lucide-react";

/**
 * Global paywall dialog listening to a custom event dispatched by the axios
 * interceptor whenever the backend returns 402 with { code: "free_limit" }.
 * A single instance mounted at the app root is enough — the interceptor fires
 * `sprintroll:free-limit` events and this component reacts.
 */

const RESOURCE_META = {
  workspace: { icon: Layers,     title: "Alcanzaste el límite de workspaces" },
  board:     { icon: LayoutGrid, title: "Alcanzaste el límite de tableros" },
  storage:   { icon: HardDrive,  title: "Alcanzaste el límite de almacenamiento" },
};

export default function FreeLimitDialog() {
  const [state, setState] = useState(null); // { resource, used, limit, message }
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => setState(e.detail || null);
    window.addEventListener("sprintroll:free-limit", handler);
    return () => window.removeEventListener("sprintroll:free-limit", handler);
  }, []);

  if (!state) return null;
  const meta = RESOURCE_META[state.resource] || RESOURCE_META.board;
  const Icon = meta.icon;

  const close = () => setState(null);
  const goToPricing = () => { close(); navigate("/pricing"); };

  return (
    <Dialog open={!!state} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent
        data-testid="free-limit-dialog"
        className="max-w-md glass-panel border-white/40 dark:border-white/10"
      >
        <DialogHeader>
          <div className="mx-auto h-14 w-14 rounded-2xl bg-gradient-to-br from-blue-500 to-sky-500 grid place-items-center shadow-lg shadow-blue-500/30 mb-3">
            <Icon className="h-7 w-7 text-white" />
          </div>
          <DialogTitle className="font-display text-xl text-center">{meta.title}</DialogTitle>
          <DialogDescription className="text-center text-sm text-muted-foreground pt-1" data-testid="free-limit-message">
            {state.message}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 rounded-xl bg-white/60 dark:bg-slate-900/40 p-4 border border-white/50 dark:border-white/10">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">
            Uso actual
          </p>
          <p className="text-2xl font-display font-semibold">
            {state.used}
            <span className="text-muted-foreground text-lg font-normal"> / {state.limit}</span>
          </p>
        </div>

        <div className="flex gap-2 mt-2">
          <Button
            variant="ghost"
            onClick={close}
            data-testid="free-limit-close"
            className="flex-1"
          >
            Ahora no
          </Button>
          <Button
            onClick={goToPricing}
            data-testid="free-limit-cta"
            className="flex-1 bg-gradient-to-br from-blue-600 to-sky-500 hover:from-blue-700 hover:to-sky-600 text-white shadow-lg shadow-blue-500/30"
          >
            <Sparkles className="h-4 w-4 mr-1.5" /> Ver planes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
